import Control from 'ol/control/Control';
import Map from 'ol/Map';
import Collection from 'ol/Collection';
import View from 'ol/View';
import MapBrowserEventType from 'ol/MapBrowserEventType';
import { DragPan } from 'ol/interaction';

// Hide the slave map.
const HIDE_SLAVE_MAP_CLASS = 'hide';

/**
 * @typedef {Object} MagnifyingGlassOptions
 * @property {import('../map').default} map The geomod map.
 * @property {import('../layers/GeomodLayerGroup').default} layers The layers in the magnify glass.
 */

/**
 * Get magnifying glass to have better precision on touch map interactions.
 */
export default class MagnifyingGlass extends Control {
  /**
     * @constructor
     * @param {MagnifyingGlassOptions} options The options
     */
  constructor(options = {}) {
    const elt = document.createElement('div');
    elt.className = 'ol-control magnifying-glass';

    options.element = elt;

    super(options);

    this.element.style.pointerEvents = null;

    /**
         * @type {MagnifyingGlass}
         */
    const this_ = this;

    /**
         * @type {GeomodLayerGroup}
         */
    const { layers } = options;

    /**
         * The control state.
         * @type {boolean}
         */
    let isShowSlaveMap = false;

    /**
         * Incatate if map id added on root element.
         * @type {boolean}
         */
    let isSlaveMapAdded = false;

    /**
         * Indicates if control is active. True to active.
         * @private
         * @type {boolean}
         */
    this.isActive_ = false;

    /**
         * On not active change, execute this fonction.
         * @private
         * @type {function}
         */
    this.hide_ = goToHideMagnifyingGlassState;

    /**
         * Show, execute this fonction.
         * @private
         * @type {function}
         */
    this.show_ = goToShowMagnifyingGlassState;

    /**
         * The map.
         * @type {import("../map").default}
         */
    const { map } = options;

    /**
         * The slave map view.
         * @type {View}
         */
    const view = new View({
      center: map.getView().getCenter(),
      zoom: map.getView().getZoom(),
    });

    /** On longpress event, show magnifying glass. */
    map.on(MapBrowserEventType.LONGPRESS, (event) => goToShowMagnifyingGlassState(event));
    /** On pointer up, hide magnifying glass. */
    map.on(MapBrowserEventType.POINTERUP, goToHideMagnifyingGlassState);
    /** On pointer down, hide magnifying glass. */
    map.on(MapBrowserEventType.POINTERDOWN, goToHideMagnifyingGlassState);

    /**
         * On pointer drag on map.
         */
    map.on(MapBrowserEventType.POINTERDRAG, (event) => setCenterAndLocationSalveView(event.coordinate, event.pixel));

    /**
         * On map move, set the set zoom level.
         */
    map.on('moveend', setZoomSlaveView);

    /**
         * Create and add the slave map on root element.
         */
    function addSlaveMap() {
      // If not timeout, map canvas display style is none.
      setTimeout(() => {
        new Map({
          controls: new Collection(),
          interactions: new Collection(),
          view,
          layers,
          target: this_.element,
        });
        
        const cross = document.createElement('div');
        cross.className = 'cross';
        const iconCross = document.createElement('span');
        iconCross.className = 'icon-cross';
        cross.appendChild(iconCross);

        this_.element.appendChild(cross);
      }, 0);
      isSlaveMapAdded = true;
    }

    /**
         * Center the slave map to the touch point and move it vertically from the finger.
         * @param {Array<number>} coordinate Projected coordinates.
         * @param {Array<number>} pixel Screen coordinate.
         */
    function setCenterAndLocationSalveView(coordinate, pixel) {
      if (isShowSlaveMap) {
        view.setCenter(coordinate);
        this_.element.style.left = `${pixel[0] - 75}px`;
        this_.element.style.top = `${pixel[1] - 75 - 100}px`;
      }
    }

    /**
         * Synchonize the slave map zoom level with the master map.
         */
    function setZoomSlaveView() {
      if (isShowSlaveMap) {
        view.setZoom(map.getView().getZoom());
      }
    }

    /**
         * On slave map show, adjuste slave map and stop pan.
         * @param {MapBrowserEvent} event The map browser event.
         */
    function goToShowMagnifyingGlassState(event) {
      if (!this_.isActive_) {
        return;
      }
      // Synchronise visible layers.
      const visibleLayers = map.getInitialLayerGroup().getVisibleLayerNameList();
      for (const layer of layers.getLayers().getArray()) {
        if (layer.getName && visibleLayers.indexOf(layer.getName()) !== -1) {
          layer.setVisible(true);
        }
      }

      isShowSlaveMap = true;
      setZoomSlaveView();
      setCenterAndLocationSalveView(event.coordinate, event.pixel);
      this_.element.classList.remove(HIDE_SLAVE_MAP_CLASS)
      if (!isSlaveMapAdded) {
        addSlaveMap();
      }
      setActiveDragPan(false);
    }

    /**
         * On slave map hide, restart pan.
         */
    function goToHideMagnifyingGlassState() {
      for (const layer of layers.getLayers().getArray()) {
        layer.setVisible(false);
      }
      isShowSlaveMap = false;
      this_.element.classList.add(HIDE_SLAVE_MAP_CLASS)
      setActiveDragPan(true);
    }

    /**
         * Activate or not the grap pan interaction.
         * @param {boolean} active Is activate grag pan interaction.
         */
    function setActiveDragPan(active) {
      for (const interaction of map.getInteractions().getArray()) {
        if (interaction instanceof DragPan) {
          interaction.setActive(active);
        }
      }
    }
  }

  /**
     * Add event to show magnifying glass.
     * @param {Observable} element The observable element.
     * @param {string} eventCode The event name.
     * @param {function(Event):MapBrowserEvent} browserEventGetter Get the map browser event.
     */
  addEventToShow(element, eventCode, browserEventGetter) {
    element.on(eventCode, (event) => this.show_(browserEventGetter(event)));
  }

  /**
     * Set if control is active.
     * @param {boolean} active True to acitvate control.
     */
  setActive(active) {
    this.isActive_ = active;
    if (!active) {
      this.hide_();
    }
  }
}
